import * as React from 'react';
import { Field, Input, makeStyles, Select, Textarea } from '@fluentui/react-components';
import { PollEventTypeCode } from '../../model/IPoll';
import { EVENT_TYPE_OPTIONS } from '../../lib/pollLabels';
import { IPollFormValues } from './pollForm';
import { IGeoState } from './useGeo';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    twoCols: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '16px',
        '@media (max-width: 767px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
});

export interface IPollDetailsFieldsProps {
    values: IPollFormValues;
    onChange: (patch: Partial<IPollFormValues>) => void;
    geo: IGeoState;
    errors?: { title?: string; eventTypeCode?: string; geoRegionId?: string };
    autoFocus?: boolean;
}

/** Title, description, event type and geography. */
const PollDetailsFields: React.FC<IPollDetailsFieldsProps> = ({ values, onChange, geo, errors, autoFocus }) => {
    const styles = useStyles();

    return (
        <div className={styles.root}>
            <Field label="Название опроса" required validationMessage={errors?.title}>
                <Input
                    value={values.title}
                    autoFocus={autoFocus}
                    maxLength={200}
                    placeholder="Например: Оценка качества коммуникации"
                    onChange={(_, data) => onChange({ title: data.value })}
                />
            </Field>

            <Field label="Описание" hint="Показывается участникам перед началом опроса.">
                <Textarea
                    value={values.description}
                    rows={3}
                    resize="vertical"
                    placeholder="Коротко опишите цель опроса"
                    onChange={(_, data) => onChange({ description: data.value })}
                />
            </Field>

            <div className={styles.twoCols}>
                <Field label="Тип мероприятия" required validationMessage={errors?.eventTypeCode}>
                    <Select
                        value={values.eventTypeCode !== undefined ? String(values.eventTypeCode) : ''}
                        onChange={(_, data) => onChange({
                            eventTypeCode: data.value === '' ? undefined : (Number(data.value) as PollEventTypeCode),
                        })}
                    >
                        <option value="">Выберите тип</option>
                        {EVENT_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={String(option.value)}>{option.label}</option>
                        ))}
                    </Select>
                </Field>

                <Field
                    label="Регион"
                    required
                    validationMessage={errors?.geoRegionId ?? geo.regionsError}
                    validationState={errors?.geoRegionId || geo.regionsError ? 'error' : 'none'}
                >
                    <Select
                        value={values.geoRegionId}
                        disabled={geo.loadingRegions}
                        onChange={(_, data) => onChange({ geoRegionId: data.value, geoTerritoryId: '' })}
                    >
                        <option value="">{geo.loadingRegions ? 'Загрузка регионов…' : 'Выберите регион'}</option>
                        {geo.regions.map((region) => (
                            <option key={region.Id} value={region.Id}>{region.Name}</option>
                        ))}
                    </Select>
                </Field>
            </div>

            {values.geoRegionId && (geo.loadingTerritories || geo.territories.length > 0) && (
                <Field label="Территория" hint="Необязательно. Уточняет регион проведения.">
                    <Select
                        value={values.geoTerritoryId}
                        disabled={geo.loadingTerritories}
                        onChange={(_, data) => onChange({ geoTerritoryId: data.value })}
                    >
                        <option value="">{geo.loadingTerritories ? 'Загрузка территорий…' : 'Не указана'}</option>
                        {geo.territories.map((territory) => (
                            <option key={territory.Id} value={territory.Id}>
                                {territory.Name}{territory.City ? ` · ${territory.City}` : ''}
                            </option>
                        ))}
                    </Select>
                </Field>
            )}
        </div>
    );
};

export default PollDetailsFields;
